import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Users, Package, ShoppingCart, LayoutDashboard, ChevronLeft, ChevronRight, ArrowLeft, X, RefreshCw, CreditCard, MessageCircle, Shield, Activity, Settings, Wifi } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { canAccessModule } from "@/lib/permissions";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_d216ca66-0733-41eb-aefb-a68564ba10f0/artifacts/fn20k37z_FINAL%20Logo%20Newsky%20png.png";

const allNavItems = [
  { label: "Dashboard", icon: LayoutDashboard, path: "/crm", module: "dashboard" },
  { label: "Clientes", icon: Users, path: "/crm/customers", module: "customers" },
  { label: "Produtos", icon: Package, path: "/crm/products", module: "products" },
  { label: "Vendas", icon: ShoppingCart, path: "/crm/sales", module: "sales" },
  { label: "Assinaturas", icon: RefreshCw, path: "/crm/subscriptions", module: "subscriptions" },
  { label: "Pagamentos", icon: CreditCard, path: "/crm/payments", module: "payments" },
  { label: "Suporte", icon: MessageCircle, path: "/crm/chat", module: "chat" },
  { label: "Consultar SIM", icon: Wifi, path: "/crm/sim-lookup", module: "customers" },
  { label: "Operadores", icon: Shield, path: "/crm/operators", module: "operators" },
  { label: "Atividades", icon: Activity, path: "/crm/audit", module: "audit" },
  { label: "Configurações", icon: Settings, path: "/crm/settings/stripe", module: "audit", adminOnly: true },
];

export default function Sidebar({ mobileOpen, onClose }) {
  const location = useLocation();
  const { user } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const role = user?.role || "operator";

  const navItems = allNavItems.filter((item) => {
    if (item.adminOnly && role !== "admin") return false;
    return canAccessModule(role, item.module);
  });

  const isActive = (path) => {
    if (path === "/crm") return location.pathname === "/crm";
    return location.pathname.startsWith(path);
  };

  const handleNavClick = () => { if (mobileOpen) onClose(); };

  return (
    <>
      {mobileOpen && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={onClose} />}

      <aside
        data-testid="crm-sidebar"
        className={`fixed left-0 top-0 h-full z-50 flex flex-col transition-all duration-300
          ${mobileOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0
          ${collapsed ? "lg:w-16" : "lg:w-60"} w-64`}
        style={{ background: "var(--crm-sidebar-bg)", borderRight: "1px solid var(--crm-border)" }}
      >
        <div className="h-16 flex items-center justify-between px-4" style={{ borderBottom: "1px solid var(--crm-border)" }}>
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="NewSky" className="h-9 w-auto flex-shrink-0" />
            {!collapsed && <span className="text-sm font-bold" style={{ color: "var(--crm-text)" }}>CRM</span>}
          </div>
          <button onClick={onClose} className="lg:hidden p-1" style={{ color: "var(--crm-text-dim)" }}>
            <X className="w-5 h-5" />
          </button>
        </div>

        <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
          {navItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            return (
              <Link
                key={item.path} to={item.path} onClick={handleNavClick}
                data-testid={`sidebar-${item.label.toLowerCase()}`}
                className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium transition-all"
                style={{
                  background: active ? "var(--crm-sidebar-active)" : "transparent",
                  color: active ? "var(--crm-accent)" : "var(--crm-text-muted)",
                  borderLeft: active ? "3px solid var(--crm-accent)" : "3px solid transparent",
                }}
              >
                <Icon className="w-5 h-5 flex-shrink-0" strokeWidth={1.5} />
                {(!collapsed || mobileOpen) && <span>{item.label}</span>}
              </Link>
            );
          })}
        </nav>

        <div className="px-3 pb-4">
          <Link to="/" onClick={handleNavClick} data-testid="sidebar-back-site"
            className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm transition-colors"
            style={{ color: "var(--crm-text-dim)" }}
          >
            <ArrowLeft className="w-5 h-5 flex-shrink-0" strokeWidth={1.5} />
            {(!collapsed || mobileOpen) && <span>Voltar ao site</span>}
          </Link>
        </div>

        <button onClick={() => setCollapsed(!collapsed)}
          className="hidden lg:flex h-12 items-center justify-center transition-colors"
          style={{ borderTop: "1px solid var(--crm-border)", color: "var(--crm-text-dim)" }}
        >
          {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
        </button>
      </aside>
    </>
  );
}
